// Plan §B-1 + §E-3 — Korean working-day utilities.
//
// Two responsibilities live here:
//   1. KOREAN_HOLIDAYS — date-string → name lookup, used by the
//      IntegratedCalendar to flag holiday cells AND by the WD+5
//      calculation in §E-3-2 (로봇 설치일 = 그릴러 발주일 + 5 영업일,
//      휴무·공휴일 제외).
//   2. addWorkingDays / isNonWorkingDay / friends — pure date math.
//
// Date format throughout is `YYYY-MM-DD` (ISO 8601 date-only). All
// computations stay in this string form so timezone / DST issues
// never enter the picture — the calendar is "what the operator sees
// on the wall in Seoul," not a wall-clock instant.
//
// Holiday data is hand-maintained for the years the app will actively
// schedule installs in. Add a new year before that calendar opens —
// missing years gracefully degrade to "no holidays" (worst case the
// install-date calculator misses one off-day, which the UI surfaces
// when the user picks a holiday cell).
// ============================================================
// KOREAN_HOLIDAYS — public + traditional + temporary holidays
// ============================================================
//
// Source: Korean government Public Holidays Act + 행정안전부 임시
// 공휴일 designations. Includes 대체공휴일 (substitute holidays
// when a fixed-date holiday falls on a weekend).
//
// Update yearly. New entries go in via a code commit so the source
// of truth stays in version control (vs an admin UI that an operator
// could accidentally clear during a busy install week).
export const KOREAN_HOLIDAYS = {
    // 2025
    '2025-01-01': '신정',
    '2025-01-28': '설날 연휴',
    '2025-01-29': '설날',
    '2025-01-30': '설날 연휴',
    '2025-03-01': '3·1절',
    '2025-05-05': '어린이날',
    '2025-05-06': '대체공휴일 (어린이날)',
    '2025-06-06': '현충일',
    '2025-08-15': '광복절',
    '2025-10-03': '개천절',
    '2025-10-05': '추석 연휴',
    '2025-10-06': '추석',
    '2025-10-07': '추석 연휴',
    '2025-10-08': '대체공휴일 (추석)',
    '2025-10-09': '한글날',
    '2025-12-25': '성탄절',
    // 2026
    '2026-01-01': '신정',
    '2026-02-16': '설날 연휴',
    '2026-02-17': '설날',
    '2026-02-18': '설날 연휴',
    '2026-03-01': '3·1절',
    '2026-03-02': '대체공휴일 (3·1절)',
    '2026-05-01': '근로자의 날', // PDF p.3 가 명시한 항목
    '2026-05-05': '어린이날',
    '2026-05-24': '석가탄신일', // 음력 4월 8일 = 5/24(일)
    '2026-05-25': '대체공휴일 (석가탄신일)',
    '2026-06-06': '현충일',
    '2026-08-15': '광복절',
    '2026-08-17': '대체공휴일 (광복절)',
    '2026-09-24': '추석 연휴',
    '2026-09-25': '추석',
    '2026-09-26': '추석 연휴',
    '2026-10-03': '개천절',
    '2026-10-05': '대체공휴일 (개천절)',
    '2026-10-09': '한글날',
    '2026-12-25': '성탄절',
    // 2027
    '2027-01-01': '신정',
    '2027-02-06': '설날 연휴',
    '2027-02-07': '설날',
    '2027-02-08': '설날 연휴',
    '2027-03-01': '3·1절',
    '2027-05-01': '근로자의 날',
    '2027-05-05': '어린이날',
    '2027-05-13': '석가탄신일',
    '2027-06-06': '현충일',
    '2027-06-07': '대체공휴일 (현충일)',
    '2027-08-15': '광복절',
    '2027-08-16': '대체공휴일 (광복절)',
    '2027-09-14': '추석 연휴',
    '2027-09-15': '추석',
    '2027-09-16': '추석 연휴',
    '2027-10-03': '개천절',
    '2027-10-04': '대체공휴일 (개천절)',
    '2027-10-09': '한글날',
    '2027-10-11': '대체공휴일 (한글날)',
    '2027-12-25': '성탄절',
};
// ============================================================
// Predicates
// ============================================================
/** True when `dateStr` (YYYY-MM-DD) is a Korean public holiday. */
export function isKoreanHoliday(dateStr) {
    return Object.prototype.hasOwnProperty.call(KOREAN_HOLIDAYS, dateStr);
}
/** Holiday name or empty string. Useful for tooltips on calendar cells. */
export function getHolidayName(dateStr) {
    return KOREAN_HOLIDAYS[dateStr] ?? '';
}
/** True when dateStr is Saturday (6) or Sunday (0). UTC-safe parsing. */
export function isWeekend(dateStr) {
    const day = parseLocalDate(dateStr).getUTCDay();
    return day === 0 || day === 6;
}
/** True when dateStr is a non-working day (weekend OR holiday). */
export function isNonWorkingDay(dateStr) {
    return isWeekend(dateStr) || isKoreanHoliday(dateStr);
}
// ============================================================
// Working-day arithmetic
// ============================================================
/**
 * Add `n` working days to `fromDateStr` (YYYY-MM-DD).
 *
 * Plan §E-3-2 (PDF p.20): "로봇 설치일 = 그릴러 발주일 + 5 영업일,
 * 휴무·공휴일 제외." n=5 here gives the minimum install date.
 *
 * Behavior:
 *   - Counts only weekdays that aren't Korean public holidays.
 *   - The starting day is NOT counted, regardless of whether it
 *     itself is a working day. (i.e. addWorkingDays('월요일', 5)
 *     returns the next Monday.)
 *   - n=0 returns the start date verbatim.
 *   - n must be non-negative; negative values throw.
 */
export function addWorkingDays(fromDateStr, n) {
    if (n < 0)
        throw new RangeError(`addWorkingDays: n must be >= 0 (got ${n})`);
    if (n === 0)
        return fromDateStr;
    const d = parseLocalDate(fromDateStr);
    let added = 0;
    // Hard upper-bound on iterations so a malformed input or a year
    // densely packed with holidays can't loop forever. 365 calendar
    // days is enough headroom for n up to ~250 working days.
    for (let i = 0; i < 365 && added < n; i++) {
        d.setUTCDate(d.getUTCDate() + 1);
        if (!isNonWorkingDay(formatLocalDate(d)))
            added += 1;
    }
    return formatLocalDate(d);
}
/**
 * §E-3-2 convenience — minimum legal install date for a given
 * grill-order date. Equivalent to `addWorkingDays(grillOrderedAt, 5)`.
 */
export function calculateMinInstallDate(grillOrderedAt) {
    return addWorkingDays(grillOrderedAt, 5);
}
/**
 * Returns the count of non-working days strictly between two
 * dates (exclusive on both ends). Useful for diagnostic UI:
 * "주문일 → 설치일 사이 휴무 N일 포함."
 */
export function countNonWorkingDaysBetween(fromDateStr, toDateStr) {
    const from = parseLocalDate(fromDateStr);
    const to = parseLocalDate(toDateStr);
    if (to <= from)
        return 0;
    let count = 0;
    const cursor = new Date(from);
    cursor.setUTCDate(cursor.getUTCDate() + 1);
    while (cursor < to) {
        if (isNonWorkingDay(formatLocalDate(cursor)))
            count += 1;
        cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return count;
}
// ============================================================
// Internal — date string ↔ Date round-trip
// ============================================================
//
// We always parse as UTC midnight so day-of-week arithmetic is
// immune to local timezone shifts. The string format (YYYY-MM-DD)
// has no timezone of its own, so this is the right interpretation.
function parseLocalDate(dateStr) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
    if (!m)
        throw new RangeError(`Invalid date string (expected YYYY-MM-DD): ${dateStr}`);
    const year = Number(m[1]);
    const month = Number(m[2]) - 1;
    const day = Number(m[3]);
    return new Date(Date.UTC(year, month, day));
}
function formatLocalDate(d) {
    const y = d.getUTCFullYear().toString().padStart(4, '0');
    const m = (d.getUTCMonth() + 1).toString().padStart(2, '0');
    const day = d.getUTCDate().toString().padStart(2, '0');
    return `${y}-${m}-${day}`;
}
//# sourceMappingURL=calendar.js.map